import React, { useState } from "react";
import { AlertTriangle, Wrench, RefreshCw, Copy, Ban, X } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

// Race Logic block-check id -> one-click fix (field = edit-form testid to scroll to).
const FIXES = {
  name_present: { label: "Add name", field: "cm-f-name" },
  start_time_valid: { label: "Fix start time", field: "cm-f-start-wrap" },
  start_time_future: { label: "Fix start time", field: "cm-f-start-wrap" },
  no_duplicate_chars: { label: "Fix keyboard", field: "cm-usecustom" },
  noncash_prize_defined: { label: "Fix prize", field: "cm-f-prize-type" },
  race_type_valid: { label: "Rebuild in Create Contest", duplicate: true },
};

export const holdIssues = (c) => {
  const rl = c.race_logic || {};
  const fromChecks = (rl.checks || []).filter((k) => k.severity === "block" && !k.ok);
  if (fromChecks.length) return fromChecks.map((k) => ({ id: k.id, detail: k.detail }));
  return (rl.blockers || []).map((d) => ({ id: null, detail: d }));
};

export const HoldReasons = ({ c, onFix, onDuplicate, onRechecked }) => {
  const [busy, setBusy] = useState(false);
  const issues = holdIssues(c);
  const recheck = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/race-logic/enforce/${c.id}?phase=scheduled`);
      if (data.status === "blocked") toast.error(`Still on hold: ${data.blockers.join("; ")}`);
      else toast.success("Readiness check passed - race released from HOLD");
      onRechecked?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="border-t border-amber-500/30 bg-amber-500/5 px-4 py-2.5 space-y-1.5" data-testid={`cm-hold-${c.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] uppercase tracking-widest text-amber-500 inline-flex items-center gap-1.5"><AlertTriangle size={11} /> Held by readiness check</span>
        <button onClick={recheck} disabled={busy} data-testid={`cm-recheck-${c.id}`}
          className="inline-flex items-center gap-1 text-[11px] font-mono text-race-sub hover:text-race-txt disabled:opacity-50">
          <RefreshCw size={11} className={busy ? "animate-spin" : ""} /> Re-check
        </button>
      </div>
      {issues.length === 0 && <div className="text-xs text-race-sub">No blocking issue recorded - run Re-check to release it.</div>}
      {issues.map((it, i) => {
        const fx = FIXES[it.id];
        return (
          <div key={i} className="flex items-center justify-between gap-3 text-xs" data-testid={`cm-hold-issue-${c.id}-${i}`}>
            <span className="text-race-txt">{it.detail}</span>
            {fx && (
              <button onClick={() => (fx.duplicate ? onDuplicate(c) : onFix(c, fx.field))} data-testid={`cm-hold-fix-${c.id}-${i}`}
                className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-amber-500/50 text-amber-500 hover:bg-amber-500/10 font-mono text-[11px] transition-colors">
                <Wrench size={11} /> {fx.label}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

const CANCELLABLE = (c) => !["completed", "cancelled", "refunded"].includes(c.status);
const GAPS = [[1, "+1 day"], [7, "+1 week"], [14, "+2 weeks"], [28, "+4 weeks"]];

export const BulkBar = ({ selected, rows, onClear, onDone }) => {
  const [mode, setMode] = useState(null);   // "dup" | "cancel"
  const [gap, setGap] = useState(7);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const picked = rows.filter((c) => selected.has(c.id));
  const cancellable = picked.filter(CANCELLABLE);
  if (!picked.length) return null;

  const runDup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/contests/bulk-duplicate", { ids: picked.map((c) => c.id), shift_days: gap });
      toast.success(`${data.created.length} race${data.created.length === 1 ? "" : "s"} duplicated${data.skipped.length ? `, ${data.skipped.length} skipped` : ""}`);
      data.skipped.forEach((s) => toast.error(`${s.name || s.id}: ${s.reason}`));
      setMode(null); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const runCancel = async () => {
    setBusy(true);
    let ok = 0, refunded = 0, failed = 0;
    for (const c of cancellable) {
      try {
        const { data } = await api.post(`/contests/${c.id}/cancel`, { note: note.trim() || null });
        ok += 1; refunded += data.refunded_registrants || 0;
      } catch { failed += 1; }
    }
    const skipped = picked.length - cancellable.length;
    toast.success(`${ok} cancelled, ${refunded} registrant${refunded === 1 ? "" : "s"} refunded${skipped ? `, ${skipped} skipped (already finished)` : ""}${failed ? `, ${failed} failed` : ""}`);
    setBusy(false); setMode(null); setNote(""); onDone();
  };

  const btn = "inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border text-xs font-mono transition-colors disabled:opacity-40";
  return (
    <div className="sticky top-2 z-20 bg-race-elevated border border-race-txt/30 rounded-sm shadow-lg px-3 py-2 space-y-2" data-testid="cm-bulkbar">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-mono text-race-txt" data-testid="cm-bulk-count">{picked.length} selected</span>
        <button onClick={() => setMode(mode === "dup" ? null : "dup")} className={`${btn} border-race-line text-race-sub hover:text-race-txt hover:border-race-txt`} data-testid="cm-bulk-duplicate"><Copy size={12} /> Duplicate</button>
        <button onClick={() => setMode(mode === "cancel" ? null : "cancel")} disabled={!cancellable.length} className={`${btn} border-red-500/50 text-red-500 hover:bg-red-500/10`} data-testid="cm-bulk-cancel"><Ban size={12} /> Cancel{cancellable.length !== picked.length ? ` (${cancellable.length})` : ""}</button>
        <button onClick={onClear} className="ml-auto inline-flex items-center gap-1 text-xs font-mono text-race-muted hover:text-race-txt" data-testid="cm-bulk-clear"><X size={12} /> Clear</button>
      </div>
      {mode === "dup" && (
        <div className="flex flex-wrap items-center gap-2 border-t border-race-line pt-2 text-xs" data-testid="cm-bulk-dup-form">
          <span className="text-race-sub">Copy forward by</span>
          <select value={gap} onChange={(e) => setGap(+e.target.value)} className="bg-race-bg border border-race-line rounded-sm px-2 py-1 text-race-txt" data-testid="cm-bulk-gap">
            {GAPS.map(([d, l]) => <option key={d} value={d}>{l}</option>)}
          </select>
          <span className="text-race-muted">Keeps each race's time of day; past races roll forward until they're in the future. Each copy is published with its own Domain Estate.</span>
          <button onClick={runDup} disabled={busy} className={`${btn} border-race-txt text-race-txt hover:bg-race-txt hover:text-race-bg`} data-testid="cm-bulk-dup-confirm">{busy ? "Duplicating…" : `Duplicate ${picked.length}`}</button>
        </div>
      )}
      {mode === "cancel" && (
        <div className="border-t border-race-line pt-2 space-y-2" data-testid="cm-bulk-cancel-form">
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Cancellation note shown to every registrant (optional)" data-testid="cm-bulk-note"
            className="w-full bg-race-bg border border-race-line rounded-sm px-2 py-1.5 text-xs text-race-txt outline-none focus:border-race-txt resize-none" />
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-race-muted">Refunds every registrant's entry fee, notifies them and frees each Domain Estate.{picked.length !== cancellable.length ? ` ${picked.length - cancellable.length} finished race(s) will be skipped.` : ""}</span>
            <button onClick={runCancel} disabled={busy} className={`${btn} ml-auto bg-red-500 border-red-500 text-white hover:bg-red-600`} data-testid="cm-bulk-cancel-confirm">{busy ? "Cancelling…" : `Confirm cancel ${cancellable.length}`}</button>
          </div>
        </div>
      )}
    </div>
  );
};
